<template>
  <div v-if="!hasPermission('List Bill To Pay')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar/>

    <div class="container-xxl">
    <div class="card mb-5">
      <div class="card-header border-0 pt-6">
        <div class="card-title">
          <h3 class="fw-bold m-0">Contas a Pagar</h3>
        </div>
        <div class="card-toolbar">
          <button v-if="hasPermission('Store Bill To Pay')" class="btn btn-primary" @click="openModal()">
            <i class="bi bi-plus-lg"></i> Nova Conta
          </button>
        </div>
      </div>

      <div class="card-body pt-0 =">
        <div class="card mb-5 mb-xl-8">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title align-items-start flex-column">
              <span class="card-label fw-bold fs-3 mb-1">Filtros</span>
            </h3>
          </div>
          <div class="card-body pt-0">
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label">Descrição</label>
                <input v-model="filters.description" class="form-control" placeholder="Buscar por descrição...">
              </div>
              <div class="col-md-2">
                <label class="form-label">Data Início</label>
                <input v-model="filters.due_date_start" type="date" class="form-control">
              </div>
              <div class="col-md-2">
                <label class="form-label">Data Fim</label>
                <input v-model="filters.due_date_end" type="date" class="form-control">
              </div>
              <div class="col-md-3">
                <label class="form-label">Status</label>
                <select v-model="filters.status" class="form-select">
                  <option value="">Todos os Status</option>
                  <option value="paid">Pago</option>
                  <option value="pending">Pendente</option>
                </select>
              </div>
              <div class="col-md-2 d-flex align-items-end gap-2">
                <button @click="fetchBills" class="btn btn-primary flex-fill">
                  <i class="bi bi-search"></i>
                </button>
                <button @click="exportCSV" class="btn btn-success flex-fill" :disabled="exporting">
                  <i class="bi bi-download"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
            <thead>
            <tr class="fw-bold text-muted">
              <th class="min-w-150px">Descrição</th>
              <th class="min-w-120px">Categoria</th>
              <th class="min-w-100px">Fornecedor</th>
              <th class="min-w-100px">Centro de Custo</th>
              <th class="min-w-100px text-end">Valor</th>
              <th class="min-w-100px">Vencimento</th>
              <th class="min-w-100px">Status</th>
              <th class="min-w-100px">Data Pagamento</th>
              <th class="min-w-100px text-end">Ações</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="bill in bills.data" :key="bill.id">
              <td>
                <span class="text-dark fw-bold d-block fs-6">{{ bill.description }}</span>
                <span v-if="bill.total_installments" class="badge badge-info d-inline-block mt-1">
                  {{ bill.installment_number || 1 }}/{{ bill.total_installments }} parcelas
                </span>
                <span v-if="bill.notes" class="text-muted fw-semibold d-block fs-7 mt-1">{{ bill.notes }}</span>
              </td>
              <td>
                <span class="text-muted fw-semibold d-block fs-7">
                  {{ bill.transaction_category?.name || '-' }}
                </span>
              </td>
              <td>
                <span class="text-muted fw-semibold d-block fs-7">
                  {{ bill.supplier?.name || '-' }}
                </span>
              </td>
              <td>
                <span class="text-muted fw-semibold d-block fs-7">
                  {{ bill.cost_center?.name || '-' }}
                </span>
              </td>
              <td class="text-end">
                <span class="text-dark fw-bold d-block fs-6">
                  R$ {{ formatCurrency(bill.amount - (bill.discount || 0)) }}
                </span>
                <span v-if="bill.discount" class="text-danger fw-semibold d-block fs-7">
                  Desconto: R$ {{ formatCurrency(bill.discount) }}
                </span>
              </td>
              <td>
                <span class="text-muted fw-semibold d-block fs-7">
                  {{ formatDate(bill.due_date) }}
                </span>
              </td>
              <td>
                <span :class="statusClass(bill.status)">
                  {{ statusLabel(bill.status) }}
                </span>
              </td>
              <td>
                <span v-if="bill.paid_at_date" class="text-muted fw-semibold d-block fs-7">
                  {{ formatDate(bill.paid_at_date) }}
                </span>
                <span v-else class="text-muted fw-semibold d-block fs-7">-</span>
              </td>
              <td class="text-end">
                <button v-if="hasPermission('Update Bill To Pay') && !isFromTissBatch(bill)"
                        class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2" 
                        @click="openModal(bill)" 
                        title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button v-if="hasPermission('Update Bill To Pay') && isFromTissBatch(bill)"
                        class="btn btn-icon btn-bg-light btn-active-color-success btn-sm me-2" 
                        @click="openModal(bill)" 
                        title="Baixar título">
                  <i class="bi bi-cash-coin"></i>
                </button>
                <button v-if="hasPermission('Delete Bill To Pay') && !isFromTissBatch(bill)"
                        class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm" 
                        @click="confirmDelete(bill.id)"
                        title="Excluir">
                  <i class="bi bi-trash"></i>
                </button>
                <span v-if="isFromTissBatch(bill)"
                      class="badge badge-light-info ms-1"
                      title="Gerada a partir de lote de guias">
                  Lote TISS
                </span>
              </td>
            </tr>
            <tr v-if="!bills.data || bills.data.length === 0">
              <td colspan="9" class="text-center text-muted py-10">
                <i class="bi bi-inbox fs-1"></i>
                <div class="mt-3">Nenhuma conta encontrada</div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <Pagination :data="bills" v-if="bills.data && bills.data.length > 0" />
      </div>
    </div>

    <BillToPayModal
      :show="showModal"
      :bill="selectedBill"
      :settlement-only="isFromTissBatch(selectedBill)"
      @close="closeModal"
      @saved="fetchBills"
    />
  </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Permission from "@/helpers/permission";
import BillToPayModal from "@/views/finance/bills_to_pay/BillToPayModal.vue";
import Toolbar from "@/components/layouts/auth/Toolbar.vue";
import Pagination from "@/components/Pagination.vue";
import moment from "moment";
import Download from "@/helpers/download";

export default {
  name: "BillToPay",
  components: {Pagination, Toolbar, BillToPayModal},
  data() {
    return {
      bills: { data: [] },
      filters: {
        description: "",
        due_date_start: "",
        due_date_end: "",
        status: ""
      },
      showModal: false,
      selectedBill: null,
      exporting: false
    };
  },
  watch: {
    '$route.query.page'() {
      this.fetchBills();
    }
  },
  methods: {
    fetchBills() {
      const params = {
        ...this.filters,
        page: this.$route.query.page || 1
      };

      http.get(`/places/${this.$store.state.auth.place.id_place}/finances/bills-to-pay`, {params})
        .then(response => {
          if (response.data?.data) {
            this.bills = response.data;
          } else if (Array.isArray(response.data)) {
            this.bills = {
              data: response.data,
              links: [],
              total: response.data.length,
              current_page: 1,
              last_page: 1
            };
          } else {
            this.bills = response.data || { data: [] };
          }
        })
        .catch(() => {
          this.bills = { data: [] };
        });
    },
    openModal(bill = null) {
      this.selectedBill = bill;
      this.showModal = true;
    },
    closeModal() {
      this.selectedBill = null;
      this.showModal = false;
    },
    confirmDelete(id) {
      const bill = (this.bills.data || []).find(b => b.id === id);
      if (bill && this.isFromTissBatch(bill)) {
        Swal.fire(
          "Ação não permitida",
          "Contas geradas a partir de lotes de guias não podem ser excluídas.",
          "warning"
        );
        return;
      }
      Swal.fire({
        title: "Tem certeza?",
        text: "Essa ação não pode ser desfeita!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar"
      }).then(result => {
        if (result.isConfirmed) {
          http.delete(`/places/${this.$store.state.auth.place.id_place}/finances/bills-to-pay/${id}`)
            .then(() => {
              this.fetchBills();
              Swal.fire("Excluído!", "A conta foi excluída com sucesso.", "success");
            })
            .catch((error) => {
              const msg = error.response?.data?.message || error.response?.data?.error || "Erro ao excluir conta.";
              Swal.fire("Erro", msg, "error");
            });
        }
      });
    },
    isFromTissBatch(bill) {
      return Boolean(bill?.from_tiss_batch || bill?.id_tiss_batch);
    },
    statusLabel(status) {
      return status === "paid" ? "Pago" : "Pendente";
    },
    statusClass(status) {
      return status === "paid" ? "badge badge-success" : "badge badge-warning";
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY');
    },
    hasPermission(permission) {
      return Permission.has(permission);
    },
    async exportCSV() {
      try {
        this.exporting = true;
        const params = {
          ...this.filters
        };
        
        const response = await http.get(
          `/places/${this.$store.state.auth.place.id_place}/finances/bills-to-pay/export`,
          { params, responseType: 'blob' }
        );
        
        Download.downloadCSV(response.data, 'contas_a_pagar.csv');
      } catch (error) {
        Swal.fire('Erro', 'Não foi possível exportar os dados.', 'error');
      } finally {
        this.exporting = false;
      }
    }
  },
  mounted() {
    this.fetchBills();
  }
};
</script>

<style scoped>
</style>
